import { Link } from "react-router-dom";
import { COMPANY_ADDRESS, COMPANY_NAME, LEGAL_NAME, SUPPORT_EMAIL } from "@/constants/company";

const UPDATED = "September 2026";

export default function Terms() {
  return (
    <div className="max-w-[860px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <div className="text-[11px] tracking-luxe uppercase text-aura-muted mb-2">Legal</div>
      <h1 className="font-serif text-4xl tracking-tight mb-2">Terms of Service</h1>
      <p className="text-xs text-aura-muted mb-8">Last updated: {UPDATED}</p>

      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        These Terms of Service ("Terms") govern your access to and use of {COMPANY_NAME} (the "Service"),
        operated by {LEGAL_NAME}, {COMPANY_ADDRESS}. {COMPANY_NAME} is part of {LEGAL_NAME}. By accessing or using the Service, you agree to these Terms. If you do
        not agree, do not use the Service.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Accounts</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        You must provide accurate information when creating an account and are responsible for
        safeguarding your credentials and all activity under your account. You must be at least 16
        years old (or the age of majority in your jurisdiction) to use the Service.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Sellers and listings</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Sellers are solely responsible for the items they list, including accuracy of descriptions,
        pricing, inventory, fulfillment, and compliance with applicable laws and the policies of any
        marketplace they connect. You may only list items you own or are authorized to sell. You
        retain ownership of content you upload and grant Shoptosa a non-exclusive license to host and
        display it to operate the Service.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Affiliate disclosure</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Shoptosa participates in affiliate programs. Some products and links on the Service are
        provided by third-party retailers and brands, and we may earn a commission when you click
        through or make a qualifying purchase on a partner's site, at no additional cost to you.
        Such purchases are completed on the retailer's website and are governed by that retailer's
        terms and policies. Product availability, pricing, and descriptions for partner items are
        controlled by the retailer and may change.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Acceptable use</h2>
      <ul className="text-sm text-aura-muted leading-relaxed mb-4 list-disc pl-5 space-y-1">
        <li>Do not use the Service for unlawful, infringing, or fraudulent purposes.</li>
        <li>Do not upload content you do not have the right to use.</li>
        <li>Do not attempt to disrupt, reverse engineer, or gain unauthorized access to the Service.</li>
        <li>Do not misrepresent affiliation or scrape the Service without permission.</li>
      </ul>

      <h2 className="font-serif text-2xl mt-10 mb-3">Payments</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        Where the Service facilitates payments, transactions are processed by third-party payment
        providers (including Stripe and Cashfree) subject to their terms. Seller subscription fees
        and AI try-on credits, if applicable, are described at the point of purchase.
        Returns, refunds, disputes, and cancellations are set out in our{" "}
        <Link to="/returns" className="link-underline">Returns, refunds &amp; cancellations</Link> policy.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Intellectual property</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        The Service, including its design, software, and branding, is owned by Shoptosa and protected
        by intellectual property laws. Third-party trademarks and product images remain the property
        of their respective owners.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Disclaimers & limitation of liability</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        The Service is provided "as is" without warranties of any kind. To the maximum extent
        permitted by law, Shoptosa is not liable for any indirect, incidental, or consequential
        damages, or for the acts, products, or content of third-party retailers and marketplaces.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Termination</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        We may suspend or terminate access to the Service at any time for conduct that violates these
        Terms or is otherwise harmful to the Service or other users.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Changes</h2>
      <p className="text-sm text-aura-muted leading-relaxed mb-4">
        We may update these Terms from time to time. Continued use after changes take effect
        constitutes acceptance of the revised Terms.
      </p>

      <h2 className="font-serif text-2xl mt-10 mb-3">Contact</h2>
      <p className="text-sm text-aura-muted leading-relaxed">
        Questions about these Terms? Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="link-underline">{SUPPORT_EMAIL}</a>
        {" "}or write to {LEGAL_NAME}, {COMPANY_ADDRESS}.
      </p>
    </div>
  );
}
